import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Search, Lock, Package, TrendingDown, Filter, Loader2 } from 'lucide-react';

export default function ChefMercuriales() {
  const [items, setItems] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filterCategorie) params.categorie = filterCategorie;
        const [itemsRes, fournRes, catRes] = await Promise.all([
          api.get('/mercuriales', { params }),
          api.get('/mercuriales/fournisseurs'),
          api.get('/mercuriales/categories'),
        ]);
        setItems(itemsRes.data);
        setFournisseurs(fournRes.data);
        setCategories(catRes.data);
      } catch {}
      setLoading(false);
    };
    fetchAll();
  }, [filterCategorie]);

  // For each product, find the best (cheapest or imposed) supplier
  const bestByProduct = {};
  items.forEach(item => {
    const key = item.produit.trim().toLowerCase();
    if (!bestByProduct[key]) {
      bestByProduct[key] = { nom: item.produit, options: [] };
    }
    bestByProduct[key].options.push(item);
  });

  // Determine recommended supplier for each product
  const recommendations = Object.values(bestByProduct).map(p => {
    const imposed = p.options.find(o => o.bloque);
    const sorted = [...p.options].sort((a, b) => a.prix_unitaire - b.prix_unitaire);
    const recommended = imposed || sorted[0];
    return {
      produit: p.nom,
      recommended,
      alternatives: sorted.filter(o => o.id !== recommended.id),
      categorie: recommended.categorie || 'Divers',
      isImposed: !!imposed,
    };
  });

  const filtered = recommendations.filter(r =>
    !search || r.produit.toLowerCase().includes(search.toLowerCase()) || r.recommended.fournisseur.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {};
  filtered.forEach(r => {
    const cat = r.categorie;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  });

  return (
    <div className="space-y-4" data-testid="chef-mercuriales">
      <div>
        <h2 className="text-lg font-semibold text-[rgb(var(--lm-text))] flex items-center gap-2">
          <Package size={20} className="text-[rgb(var(--lm-green))]" /> Mercuriales
        </h2>
        <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-0.5">
          Ou commander chaque produit — prix et fournisseurs
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un produit..."
            className="h-8 text-sm bg-[rgb(var(--lm-bg))] pl-9" data-testid="chef-search" />
        </div>
        <select value={filterCategorie} onChange={e => setFilterCategorie(e.target.value)}
          className="h-8 text-xs bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-[rgb(var(--lm-text))]" data-testid="chef-filter-cat">
          <option value="">Toutes categories</option>
          {categories.map((c, i) => <option key={i} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Products */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-[rgb(var(--lm-green))]" size={24} />
        </div>
      ) : Object.keys(grouped).length > 0 ? (
        Object.entries(grouped).sort().map(([cat, catItems]) => (
          <div key={cat}>
            <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-2 flex items-center gap-2">
              <Filter size={10} /> {cat} ({catItems.length})
            </h3>
            <div className="space-y-1.5">
              {catItems.map((rec, i) => (
                <div key={i} className="lm-card p-3 flex items-center justify-between" data-testid={`chef-produit-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-[rgb(var(--lm-text))] truncate">{rec.produit}</p>
                      {rec.isImposed && <Lock size={10} className="text-lemas-gold shrink-0" title="Fournisseur impose par le patron" />}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <TrendingDown size={10} className="text-[rgb(var(--lm-green))]" />
                      <span className="text-xs text-[rgb(var(--lm-green))] font-medium">{rec.recommended.fournisseur}</span>
                      <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">· {rec.recommended.unite}</span>
                    </div>
                    {rec.alternatives.length > 0 && (
                      <div className="flex gap-2 mt-1">
                        {rec.alternatives.map((alt, j) => (
                          <span key={j} className="text-[10px] text-[rgb(var(--lm-text-muted))]">
                            {alt.fournisseur}: {alt.prix_unitaire.toFixed(2)}€
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="font-mono text-sm text-[rgb(var(--lm-green))]">{rec.recommended.prix_unitaire.toFixed(2)}€</p>
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">/{rec.recommended.unite}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      ) : (
        <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-8">
          Aucune mercuriale. Le patron doit importer les catalogues fournisseurs.
        </p>
      )}
    </div>
  );
}
